import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ActiveSession {
  id: string;
  session_token: string;
  medewerker_id: string;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string | null;
  expires_at: string;
  medewerker_naam: string;
  medewerker_username: string | null;
}

const fetchActiveSessions = async (): Promise<ActiveSession[]> => {
  // Fetch active, non-expired sessions
  const { data: sessions, error: sessionsError } = await supabase
    .from("auth_sessions")
    .select("id, session_token, medewerker_id, user_agent, ip_address, created_at, expires_at")
    .eq("is_active", true)
    .gt("expires_at", new Date().toISOString())
    .order("created_at", { ascending: false });

  if (sessionsError) throw sessionsError;
  if (!sessions || sessions.length === 0) return [];

  // Get unique medewerker IDs
  const medewerkerIds = [...new Set(sessions.map((s) => s.medewerker_id).filter(Boolean))] as string[];

  // Fetch medewerker names
  const { data: medewerkers } = await supabase
    .from("medewerkers")
    .select("id, naam, username")
    .in("id", medewerkerIds);

  const medewerkerMap = new Map(
    (medewerkers || []).map((m) => [m.id, { naam: m.naam, username: m.username }])
  );

  return sessions.map((s) => ({
    ...s,
    medewerker_naam: medewerkerMap.get(s.medewerker_id || "")?.naam || "Onbekend",
    medewerker_username: medewerkerMap.get(s.medewerker_id || "")?.username || null,
  }));
};

export const useActiveSessions = () => {
  const queryClient = useQueryClient();

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["active-sessions"],
    queryFn: fetchActiveSessions,
    refetchInterval: 30000,
  });

  const terminateSession = useMutation({
    mutationFn: async (sessionToken: string) => {
      const { data, error } = await supabase.rpc("deactivate_session", {
        p_session_token: sessionToken,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Sessie beëindigd");
      queryClient.invalidateQueries({ queryKey: ["active-sessions"] });
    },
    onError: () => {
      toast.error("Kon sessie niet beëindigen");
    },
  });

  return { sessions, isLoading, terminateSession };
};
